import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Typography,
  Alert,
  InputLabel,
  FormHelperText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Fab
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSkillSheetMainStepper } from '../context/SkillSheetMainStepperContext';

export default function CategoryContentForm({
  title,
  fieldName,
  masterDataKey,
  stepIndex,
  isEdit = false
}) {
  // コンテキストの取得を条件分岐で行う
  let stepperContext;

  if (isEdit) {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('編集用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  } else {
    try {
      stepperContext = useSkillSheetMainStepper();
    } catch (error) {
      console.error('作成用コンテキストの取得に失敗しました:', error);
      throw error;
    }
  }

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    fetchMasterData,
    masterData,
    validateStep
  } = stepperContext;

  // バリデーション表示の状態
  const [showValidation, setShowValidation] = useState(false);
  // 削除確認ダイアログの状態
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, index: null });
  // 新しく追加された要素へのスクロール用ref
  const formRefs = useRef([]);

  // フォームの値を監視
  const operations = watch(fieldName) || [{ operation_id: '', content: '' }];

  // マスタデータを取得
  useEffect(() => {
    fetchMasterData(masterDataKey);
  }, [fetchMasterData, masterDataKey]);

  const getSelectedCategoryIds = () => {
    return operations
      .map(operation => operation.operation_id)
      .filter(id => id && id !== '')
      .map(id => String(id));
  };

  const getAvailableCategories = (currentIndex) => {
    const selectedIds = getSelectedCategoryIds();
    const currentOperationId = operations[currentIndex]?.operation_id;

    // 現在選択されているIDは除外対象から除く
    const excludeIds = selectedIds.filter(id => id !== String(currentOperationId));

    if (masterData[masterDataKey]) {
      return masterData[masterDataKey].filter(item =>
        !excludeIds.includes(String(item.id))
      );
    }
    return [];
  };

  // バリデーション関数
  const validateForm = () => {
    let hasError = false;
    clearErrors();

    // operationsのバリデーション
    operations.forEach((operation, index) => {
      const hasOperationId = operation.operation_id && operation.operation_id !== '';
      const hasContent = operation.content && operation.content !== '';

      // カテゴリーまたは業務内容のどちらか一方が入力されている場合、もう一方も必須
      if (hasOperationId || hasContent) {
        if (!hasOperationId) {
          setError(`${fieldName}.${index}.operation_id`, {
            type: 'required',
            message: 'カテゴリーを入力してください'
          });
          hasError = true;
        }
        if (!hasContent) {
          setError(`${fieldName}.${index}.content`, {
            type: 'required',
            message: '業務内容を入力してください'
          });
          hasError = true;
        }
      }
    });

    if (hasError) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }

    return !hasError;
  };

  // バリデーションエラーが表示された時の処理
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setShowValidation(true);
      // エラーがある場合、最初のエラーフィールドまでスクロール
      setTimeout(() => {
        const firstErrorField = document.querySelector('[data-error="true"]');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } else {
      setShowValidation(false);
    }
  }, [errors]);

  // フォームバリデーションを外部から呼び出せるように登録
  useEffect(() => {
    if (validateStep) {
      validateStep(stepIndex, validateForm);
    }
  }, [validateStep, operations, stepIndex]);

  const handleChange = (index, fieldNameSuffix, value) => {
    const newOperations = [...operations];
    newOperations[index] = { ...newOperations[index], [fieldNameSuffix]: value };
    setValue(fieldName, newOperations);

    // 変更時に該当フィールドのエラーをクリア
    const fieldErrors = errors?.[fieldName];
    if (fieldErrors && fieldErrors[index] && fieldErrors[index][fieldNameSuffix]) {
      clearErrors(`${fieldName}.${index}.${fieldNameSuffix}`);
    }
  };

  // 要素を追加
  const handleAdd = () => {
    const newOperations = [...operations, { operation_id: '', content: '' }];
    setValue(fieldName, newOperations);

    // 新しい要素にスクロール
    setTimeout(() => {
      const newIndex = newOperations.length - 1;
      if (formRefs.current[newIndex]) {
        formRefs.current[newIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  // 削除前の確認
  const handleDeleteConfirm = (index) => {
    const operation = operations[index];
    const hasContent = (operation.operation_id && operation.operation_id !== '') ||
                      (operation.content && operation.content !== '');

    if (hasContent) {
      setDeleteConfirmDialog({ open: true, index });
    } else {
      handleDelete(index);
    }
  };

  // 要素を削除
  const handleDelete = (index) => {
    const newOperations = operations.filter((_, i) => i !== index);
    setValue(fieldName, newOperations.length > 0 ? newOperations : []);
    setDeleteConfirmDialog({ open: false, index: null });

    // 削除に関連するエラーをクリア
    const operationIdErrorKey = `${fieldName}.${index}.operation_id`;
    const contentErrorKey = `${fieldName}.${index}.content`;
    clearErrors(operationIdErrorKey);
    clearErrors(contentErrorKey);
  };

  // バリデーションエラーの取得
  const getFieldError = (index, fieldNameSuffix) => {
    // react-hook-formのエラーオブジェクトの構造に合わせてエラーを取得
    const fieldErrors = errors?.[fieldName];
    if (showValidation && fieldErrors && fieldErrors[index] && fieldErrors[index][fieldNameSuffix]) {
      return fieldErrors[index][fieldNameSuffix];
    }
    return null;
  };

  // バリデーションエラーメッセージの生成
  const validationErrors = [];
  if (showValidation && errors?.[fieldName]) {
    operations.forEach((_, index) => {
      const operationIdError = getFieldError(index, 'operation_id');
      const contentError = getFieldError(index, 'content');
      if (operationIdError) validationErrors.push(operationIdError.message);
      if (contentError) validationErrors.push(contentError.message);
    });
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ marginBottom: '24px' }}>
        {title}
      </Typography>

      {/* バリデーションエラーメッセージの表示 */}
      {showValidation && validationErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {validationErrors.map((error, index) => (
            <div key={index}>{error}</div>
          ))}
        </Alert>
      )}

      {/* 項目の一覧 */}
      {operations.length > 0 ? (
        operations.map((operation, index) => {
          const operationIdError = getFieldError(index, 'operation_id');
          const contentError = getFieldError(index, 'content');

          return (
            <Box
              key={index}
              ref={el => formRefs.current[index] = el}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                p: 3,
                mb: 3,
                position: 'relative'
              }}
              data-error={!!(operationIdError || contentError)}
            >
              {/* 削除ボタン */}
              <IconButton
                onClick={() => handleDeleteConfirm(index)}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: '#dc2626',
                  '&:hover': {
                    backgroundColor: '#fef2f2',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              <Grid container spacing={3}>
                {/* カテゴリー */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>カテゴリー</Typography>
                    <FormControl fullWidth error={!!operationIdError}>
                      <InputLabel>カテゴリーを選択</InputLabel>
                      <Select
                        value={operation.operation_id || ''}
                        onChange={(e) => handleChange(index, 'operation_id', e.target.value)}
                        label="カテゴリーを選択"
                      >
                        {getAvailableCategories(index).map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {operationIdError && <FormHelperText>{operationIdError.message}</FormHelperText>}
                    </FormControl>
                  </Box>
                </Grid>

                {/* 業務内容 */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography sx={{ mb: 1 }}>業務内容</Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={7}
                      value={operation.content || ''}
                      onChange={(e) => handleChange(index, 'content', e.target.value)}
                      placeholder="具体的な業務内容を入力してください"
                      variant="outlined"
                      error={!!contentError}
                      helperText={contentError?.message}
                      sx={{
                        '& textarea:focus': {
                          boxShadow: 'none',
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          );
        })
      ) : (
        // すべてのフォームが削除された場合のメッセージ
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {title}の項目を追加してください
          </Typography>
        </Box>
      )}

      {/* 追加ボタン */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Fab
          color="primary"
          onClick={handleAdd}
          sx={{
            bgcolor: '#4F46E5',
            '&:hover': {
              bgcolor: '#4338CA',
            }
          }}
        >
          <AddIcon />
        </Fab>
      </Box>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => setDeleteConfirmDialog({ open: false, index: null })}
      >
        <DialogTitle>削除の確認</DialogTitle>
        <DialogContent>
          <Typography>
            本当に削除しますか？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, index: null })}
            color="primary"
          >
            キャンセル
          </Button>
          <Button
            onClick={() => handleDelete(deleteConfirmDialog.index)}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
